import { useState, useCallback } from 'react';
import { useOkapiKy } from '@folio/stripes/core';

const useBulkEditInstances = () => {
  const ky = useOkapiKy();
  
  const [state, setState] = useState({
    isProcessing: false,
    progress: {
      total: 0,
      processed: 0,
      successful: 0,
      failed: 0,
      currentItem: '',
      percentage: 0,
    },
    results: null,
    error: null,
  });

  const applyUpdates = useCallback((instance, updates) => {
    const updated = { ...instance };
    
    // فقط الحقول التي لها قيم فعلية
    Object.keys(updates).forEach(key => {
      const value = updates[key];
      
      // تجاهل الحقول الفارغة
      if (value === undefined || value === null || value === '') {
        return;
      }
      
      // للـ arrays: تحديث فقط إذا كان غير فارغ
      if (Array.isArray(value)) {
        if (value.length > 0) {
          updated[key] = value;
        }
      } 
      // للـ objects: تحديث فقط إذا كان غير فارغ
      else if (typeof value === 'object') {
        if (Object.keys(value).length > 0) {
          updated[key] = value;
        }
      }
      // للقيم البسيطة: تحديث مباشرة
      else {
        updated[key] = value;
      }
    });
    
    // الحفاظ على الحقول المهمة
    updated.id = instance.id;
    updated._version = instance._version;
    updated.hrid = instance.hrid;
    
    return updated;
  }, []);

  const processBulkEdit = useCallback(async (instanceIds, updates) => {
    setState(prev => ({
      ...prev,
      isProcessing: true,
      progress: {
        total: instanceIds.length,
        processed: 0,
        successful: 0,
        failed: 0,
        currentItem: '',
        percentage: 0,
      },
      results: null,
      error: null,
    }));

    const failures = [];
    let successful = 0;

    for (let i = 0; i < instanceIds.length; i++) {
      const instanceId = instanceIds[i];
      
      try {
        const instance = await ky.get(`inventory/instances/${instanceId}`).json();
        
        const updatedInstance = applyUpdates(instance, updates);
        
        await ky.put(`inventory/instances/${instanceId}`, {
          json: updatedInstance,
        });
        
        successful++;
        
        setState(prev => ({
          ...prev,
          progress: {
            ...prev.progress,
            processed: i + 1,
            successful,
            currentItem: `Updated: ${instance.title}`,
            percentage: Math.round(((i + 1) / instanceIds.length) * 100),
          },
        }));
      } catch (error) {
        const errorMessage = error.message || 'Unknown error';
        
        failures.push({
          instanceId,
          title: 'Unknown',
          error: errorMessage,
        });
        
        setState(prev => ({
          ...prev,
          progress: {
            ...prev.progress,
            processed: i + 1,
            failed: prev.progress.failed + 1,
            currentItem: `Failed: ${errorMessage}`,
            percentage: Math.round(((i + 1) / instanceIds.length) * 100),
          },
        }));
      }
    }

    const results = {
      total: instanceIds.length,
      successful,
      failed: failures.length,
      failures,
    };

    setState(prev => ({
      ...prev,
      isProcessing: false,
      results,
    }));

    return results;
  }, [ky, applyUpdates]);

  const reset = useCallback(() => {
    setState({
      isProcessing: false,
      progress: {
        total: 0,
        processed: 0,
        successful: 0,
        failed: 0,
        currentItem: '',
        percentage: 0,
      },
      results: null,
      error: null,
    });
  }, []);

  return {
    ...state,
    processBulkEdit,
    reset,
  };
};

export default useBulkEditInstances;
